import React, { useMemo } from "react";
import { Button, Divider, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DropdownInput } from "@/components/Inputs";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { ApiKeySectionProps } from "./ApiKeySection.types";
import { NEW_KEY, AUTOGENERATED, USER_INPUT, FIX, InputFields } from "../../AccountModal.types";
import { FixSessionType } from "@pulse-ui/codegen/generated_ts/types";
import {
    CapabilitiesMultipleValuesInput,
    DefaultTextAreaInput,
    DefaultTextInput,
    FreeSoloDropdownInput,
    NamespacesMultipleValuesInput,
    VenueApiKeyFlagMultipleValuesInput,
} from "@/components/Inputs";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ApiKeyCapability, IVenueApiKey, K8Namespace } from "@pulse-ui/codegen/generated_ts/authn";
import { IDropcopyIp } from "../../AccountModal.types";

const fixSessionTypes: ISelectOption[] = Object.keys(FixSessionType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({ label: key, value: key }));
fixSessionTypes.unshift({ label: "None", value: "" });

const apiKeyInputFields: InputFields[] = [
    {
        name: "key_id",
        label: "Key ID",
        group: AUTOGENERATED,
        type: "text",
        editable: false,
    },
    {
        name: "account_id",
        label: "Account ID",
        group: AUTOGENERATED,
        type: "text",
        editable: false,
    },
    {
        name: "portfolio_id",
        label: "Portfolio ID",
        group: AUTOGENERATED,
        type: "text",
        editable: false,
    },
    {
        name: "access_key",
        label: `Access Key*`,
        group: USER_INPUT,
        type: "text",
        editable: true,
    },
    {
        name: "passphrase",
        label: "Passphrase",
        group: USER_INPUT,
        type: "password",
        editable: true,
    },
    {
        name: "secret_key",
        label: `Secret Key*`,
        group: USER_INPUT,
        type: "textarea",
        editable: true,
    },
    {
        name: "connection",
        label: "Connection",
        group: FIX,
        type: "text",
        editable: true,
    },
    {
        name: "venue",
        label: `Venue*`,
        group: USER_INPUT,
        type: "freesolo",
        editable: true,
    },
    {
        name: "sender_comp_id",
        label: "Sender Comp ID",
        group: FIX,
        type: "text",
        editable: true,
    },
    {
        name: "api_capabilities",
        label: "API Capabilities*",
        group: USER_INPUT,
        type: "capabilities_multiple",
        editable: true,
    },
    {
        name: "venue_api_key_flags",
        label: "Venue Api Key Flags",
        group: USER_INPUT,
        type: "venue_api_key_flag_multiple",
        editable: true,
    },
    {
        name: "allow_ip",
        label: `Allow IP*`,
        group: USER_INPUT,
        type: "freesolo",
        editable: true,
    },
    {
        name: "target_comp_id",
        label: "Target Comp ID",
        group: FIX,
        type: "text",
        editable: true,
    },
    {
        name: "fix_session_type",
        label: "Fix Session Type",
        group: FIX,
        type: "freesolo",
        options: fixSessionTypes,
        editable: true,
    },
    {
        name: "availability_zone",
        label: "Availability Zone",
        group: USER_INPUT,
        type: "text",
        editable: true,
    },
    {
        name: "namespaces",
        label: "K8 Namespaces",
        group: USER_INPUT,
        type: "namespaces_multiple",
        editable: true,
    },
];

const renderApiKeyInputField = ({
    field,
    newApiKey,
    ipAddresses,
    venueCodes,
    isEditMode,
    handleNewApiKeyChange,
    handleNewApiKeyFreeSoloChange,
    handleNewApiKeyVenueApiKeyFlagChange,
    handleNewApiKeyNamespacesChange,
    handleNewApiKeyCapabilitiesChange,
    copyToClipboard,
    col_size,
}: {
    field: InputFields;
    newApiKey: IVenueApiKey | null;
    ipAddresses: IDropcopyIp[];
    venueCodes: string[];
    isEditMode: boolean;
    handleNewApiKeyChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleNewApiKeyFreeSoloChange: (value: any, name: any) => void;
    handleNewApiKeyVenueApiKeyFlagChange: (
        e: React.SyntheticEvent<Element, Event>,
        value: any,
        reason: any
    ) => void;
    handleNewApiKeyNamespacesChange: (
        _: React.SyntheticEvent<Element, Event>,
        value: any
    ) => void;
    handleNewApiKeyCapabilitiesChange: (
        _: React.SyntheticEvent<Element, Event>,
        value: any
    ) => void;
    copyToClipboard: (text: string, name: string) => Promise<void>;
    col_size?: number;
}): any => {
    const { name, label, type } = field;

    let parsedIpAddresses: ISelectOption[] = [];
    let parsedVenueCodes: ISelectOption[] = [];

    if (type === "freesolo") {
        if (name === "allow_ip") {
            for (let address of ipAddresses) {
                parsedIpAddresses.push({
                    label: `${address.label} [${address.ip}]`,
                    value: address.ip,
                });
            }
        }

        if (name === "venue") {
            for (let venue of venueCodes) {
                parsedVenueCodes.push({
                    label: venue,
                    value: venue,
                });
            }
        }
    }

    return type === "text" || type === "password" ? (
        <DefaultTextInput
            key={name}
            name={name}
            label={label}
            value={newApiKey && (newApiKey as any)?.[name]}
            onChange={handleNewApiKeyChange}
            containerClassNames={`pulse-default-text-input pulse-br-sm pulse-mb-2 pulse-col-xs-${col_size}`}
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
            type={type}
            disabled={!isEditMode || !field.editable}
            copyIcon={<ContentCopyIcon />}
            copyToClipboard={copyToClipboard}
        />
    ) : type === "venue_api_key_flag_multiple" ? (
        <VenueApiKeyFlagMultipleValuesInput
            key={name}
            label={label}
            value={newApiKey && (newApiKey as any)?.[name]}
            onChange={handleNewApiKeyVenueApiKeyFlagChange}
            containerClassNames="venue-api-key-flag-multiple-values-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
            disabled={!isEditMode || !field.editable}
        />
    ) : type === "namespaces_multiple" ? (
        <NamespacesMultipleValuesInput
            key={name}
            label={label}
            value={
                newApiKey &&
                (newApiKey as any)?.[name].map(
                    (namespace: K8Namespace) => K8Namespace[namespace]
                )
            }
            onChange={handleNewApiKeyNamespacesChange}
            containerClassNames="namespaces-multiple-values-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
            disabled={!isEditMode || !field.editable}
        />
    ) : type === "capabilities_multiple" ? (
        <CapabilitiesMultipleValuesInput
            key={name}
            label={label}
            value={
                newApiKey &&
                (newApiKey as any)?.[name].map(
                    (capability: ApiKeyCapability) => ApiKeyCapability[capability]
                )
            }
            onChange={handleNewApiKeyCapabilitiesChange}
            containerClassNames="capabilities-multiple-values-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
            disabled={!isEditMode || !field.editable}
        />
    ) : type === "textarea" ? (
        <DefaultTextAreaInput
            key={name}
            name={name}
            label={label}
            value={newApiKey && (newApiKey as any)?.[name]}
            onChange={handleNewApiKeyChange}
            containerClassNames={`pulse-default-text-input pulse-br-sm pulse-mb-2 pulse-col-xs-${col_size}`}
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            inputClassNames="pulse-bg-color--background-800 pulse-typography-body-2"
            disabled={!isEditMode || !field.editable}
            copyIcon={<ContentCopyIcon />}
            copyToClipboard={copyToClipboard}
            rows={5}
        />
    ) : name === "venue" ? (
        <FreeSoloDropdownInput
            key={name}
            name={name}
            label={label}
            options={parsedVenueCodes}
            value={newApiKey && (newApiKey as any)?.["venue"]}
            onChange={handleNewApiKeyFreeSoloChange}
            containerClassNames="free-solo-dropdown-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            disabled={!isEditMode || !field.editable}
        />
    ) : name === "allow_ip" ? (
        <FreeSoloDropdownInput
            key={name}
            name={name}
            label={label}
            options={parsedIpAddresses}
            value={newApiKey && (newApiKey as any)?.["allow_ip"]}
            onChange={handleNewApiKeyFreeSoloChange}
            containerClassNames="free-solo-dropdown-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            disabled={!isEditMode || !field.editable}
        />
    ) : name === "fix_session_type" ? (
        <FreeSoloDropdownInput
            key={name}
            name={name}
            label={label}
            options={fixSessionTypes}
            value={newApiKey && (newApiKey as any)?.["fix_session_type"]}
            onChange={handleNewApiKeyFreeSoloChange}
            containerClassNames="free-solo-dropdown-input pulse-br-sm pulse-mb-2 pulse-col-xs-6"
            labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            disabled={!isEditMode || !field.editable}
        />
    ) : (
        <DropdownInput
            value={(newApiKey && (newApiKey as any)?.[name]) ?? null}
            label={label}
            onChange={handleNewApiKeyChange}
            name={name}
            options={field?.options || []}
            formInput
            containerClassNames="pulse-col-xs-6"
            disabled={!isEditMode || !field.editable}
        />
    );
};

export const ApiKeySection: React.FC<ApiKeySectionProps> = ({
    accountId,
    apiKeys,
    selectedApiKey,
    newApiKey,
    venueCodes,
    ipAddresses,
    buttonDisabled,
    displayForm,
    onSelectApiKey,
    onSaveApiKey,
    onDeleteApiKey,
    onNewApiKeyChange,
    onNewApiKeyFreeSoloChange,
    onNewApiKeyVenueApiKeyFlagChange,
    onNewApiKeyNamespacesChange,
    onNewApiKeyCapabilitiesChange,
    onShowForm,
    copyToClipboard,
}) => {
    const fieldsHash = useMemo(() => {
        const hash: Map<string, InputFields[]> = new Map();
        apiKeyInputFields.forEach((field) => {
            if (!hash.has(field.group)) {
                hash.set(field.group, [field]);
            } else {
                const existingFields = hash.get(field.group) || [];
                hash.set(field.group, [...existingFields, field]);
            }
        });
        return hash;
    }, []);

    const sortedVenueCodes = [...venueCodes].sort((a, b) => a.localeCompare(b));

    const renderFieldsByGroup = (groupType: string, col_size?: number) => {
        const fields = fieldsHash.get(groupType);

        if (!fields || fields.length === 0) {
            return null;
        }

        return fields.map((field: InputFields) =>
            renderApiKeyInputField({
                field,
                newApiKey,
                ipAddresses,
                venueCodes: sortedVenueCodes,
                isEditMode: selectedApiKey === NEW_KEY,
                handleNewApiKeyChange: onNewApiKeyChange,
                handleNewApiKeyFreeSoloChange: onNewApiKeyFreeSoloChange,
                handleNewApiKeyVenueApiKeyFlagChange: onNewApiKeyVenueApiKeyFlagChange,
                handleNewApiKeyNamespacesChange: onNewApiKeyNamespacesChange,
                handleNewApiKeyCapabilitiesChange: onNewApiKeyCapabilitiesChange,
                copyToClipboard,
                col_size: col_size || 6,
            })
        );
    };

    return (
        <>
            <Divider className="account-modal-divider" />

            {displayForm ? (
                <>
                    <div
                        className="pulse-row"
                        style={{ justifyContent: "space-between" }}
                    >
                        <p className="pulse-typography-headline-6 pulse-color--background-100 pulse-mt-2 pulse-mb-2">
                            API Keys
                        </p>

                        {selectedApiKey !== NEW_KEY && (
                            <IconButton
                                onClick={() => onDeleteApiKey(accountId, selectedApiKey)}
                                className="account-modal-dialog-content-apikey-delete-button"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </div>

                    <DropdownInput
                        value={selectedApiKey}
                        label={"Select API Key to edit/delete or add a new one"}
                        onChange={(e: any) => {
                            onSelectApiKey(e.target.value);
                        }}
                        name="selected_api_key"
                        options={[
                            { label: "Add New API Key", value: NEW_KEY },
                            ...apiKeys.map(
                                (api_key: any): ISelectOption => ({
                                    label: api_key.venue_api_key_id,
                                    value: api_key.venue_api_key_id,
                                })
                            ),
                        ]}
                        formInput
                        containerClassNames=""
                        omitEmptyOption
                        disabled={false}
                    />

                    <div className="api-key-non-grouped-form-fields pulse-mt-2 pulse-mb-2 pulse-row pulse-gap-1">
                        {renderFieldsByGroup(AUTOGENERATED, 4)}
                    </div>

                    <Divider className="account-modal-divider pulse-mt-2 pulse-mb-2" />

                    <div className="api-key-non-grouped-form-fields pulse-mt-2 pulse-mb-2 pulse-row pulse-gap-1">
                        {renderFieldsByGroup(USER_INPUT)}
                    </div>

                    <fieldset
                        className="pulse-row pulse-gap-1 pulse-p-1 pulse-ml-0 pulse-mr-0 pulse-color--background-50 api-keys-form-container"
                        style={{
                            border: "2px solid",
                        }}
                    >
                        <legend className="pulse-color--background-50">FIX</legend>
                        {renderFieldsByGroup(FIX)}
                    </fieldset>

                    {selectedApiKey === NEW_KEY && (
                        <div className="api-key-save-input-container pulse-mt-2 pulse-mb-1">
                            <Button
                                variant="contained"
                                onClick={onSaveApiKey}
                                size="small"
                                disabled={buttonDisabled}
                                sx={{
                                    backgroundColor: "#119436",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#16be45",
                                        color: "#fff",
                                    },
                                    "&:disabled": {
                                        backgroundColor: "#627195",
                                        color: "#091229",
                                        opacity: 0.5,
                                    },
                                }}
                            >
                                Save API Key
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <Button
                    variant="contained"
                    onClick={onShowForm}
                    size="small"
                    sx={{
                        marginTop: "0.75rem",
                        backgroundColor: "#119436",
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: "#16be45",
                            color: "#fff",
                        },
                    }}
                >
                    Add API Key
                </Button>
            )}
        </>
    );
};
